import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, CheckCircle2, Activity, MessageSquare } from "lucide-react";

const SEVERITY_META = {
  info: { label: "Info", cls: "bg-blue-50 text-blue-800 border-blue-300" },
  minor: { label: "Mineur", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  major: { label: "Majeur", cls: "bg-orange-50 text-orange-800 border-orange-300" },
  critical: { label: "Critique", cls: "bg-red-50 text-red-800 border-red-300" },
};
const STATUS_META = {
  investigating: { label: "Investigation", cls: "bg-amber-100 text-amber-900" },
  identified: { label: "Identifié", cls: "bg-blue-100 text-blue-900" },
  monitoring: { label: "Monitoring", cls: "bg-sky-100 text-sky-900" },
  resolved: { label: "Résolu", cls: "bg-emerald-100 text-emerald-900" },
};

export default function AdminIncidents() {
  const [catalog, setCatalog] = useState({ services: [], severities: [], statuses: [] });
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [addingUpdate, setAddingUpdate] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: r }] = await Promise.all([
        api.get("/incidents/catalog"),
        api.get("/admin/incidents"),
      ]);
      setCatalog(c);
      setItems(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing({ id: null });
    setForm({
      title: "",
      service: catalog.services[0]?.key || "chef_portal",
      severity: "minor",
      status: "investigating",
      message: "",
      show_on_landing: true,
      show_in_portals: true,
    });
  };
  const openEdit = (r) => {
    setEditing({ id: r.id });
    setForm({
      title: r.title || "",
      service: r.service,
      severity: r.severity,
      status: r.status,
      message: r.message || "",
      show_on_landing: r.show_on_landing !== false,
      show_in_portals: r.show_in_portals !== false,
    });
  };
  const save = async () => {
    if (!form.title?.trim()) { toast.error("Le titre est obligatoire"); return; }
    try {
      if (editing?.id) {
        await api.patch(`/admin/incidents/${editing.id}`, form);
        toast.success("Incident mis à jour");
      } else {
        await api.post("/admin/incidents", form);
        toast.success("Incident créé");
      }
      setEditing(null); setForm(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Supprimer l'incident "${r.title}" ?`)) return;
    await api.delete(`/admin/incidents/${r.id}`); toast.success("Supprimé"); load();
  };
  const addUpdate = async () => {
    if (!addingUpdate?.message?.trim()) return;
    try {
      await api.post(`/admin/incidents/${addingUpdate.id}/updates`, {
        status: addingUpdate.status,
        message: addingUpdate.message,
      });
      toast.success("Update ajouté");
      setAddingUpdate(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resolve = async (r) => {
    try {
      await api.post(`/admin/incidents/${r.id}/updates`, { status: "resolved", message: "Incident résolu." });
      toast.success("Marqué résolu"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const active = items.filter((i) => !i.resolved);
  const resolved = items.filter((i) => i.resolved);

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Statut services" title="Centre d'incidents" sub="Créez des bandeaux publics (landing) et intra-app (portails) pour prévenir vos utilisateurs des dégradations en cours." />
      <div className="p-6 lg:p-10 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm text-[#495057]">
            {active.length} incident(s) actif(s) · {resolved.length} archivé(s)
          </div>
          <div className="flex gap-2">
            <a href="/status" target="_blank" rel="noreferrer" className="inline-flex items-center h-9 px-3 border border-[#0A0A0A] text-[10px] uppercase tracking-wider font-bold hover:bg-[#0A0A0A] hover:text-white"><Activity className="h-3 w-3 mr-1" />Voir la page publique</a>
            <Button onClick={openNew} data-testid="btn-new-incident" className="bg-[#E60000] text-white">
              <Plus className="h-4 w-4 mr-2" /> Ouvrir un incident
            </Button>
          </div>
        </div>

        {/* Actifs */}
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#E60000] mb-2 flex items-center gap-2"><AlertTriangle className="h-3 w-3" />Actifs</div>
          <div className="grid gap-3" data-testid="active-incidents">
            {active.length === 0 ? (
              <div className="p-6 bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Tous les services fonctionnent normalement.</div>
            ) : active.map((r) => (
              <IncidentCard key={r.id} r={r} catalog={catalog} onEdit={openEdit} onDelete={del} onResolve={resolve} onAddUpdate={setAddingUpdate} />
            ))}
          </div>
        </div>

        {/* Résolus */}
        {resolved.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#059669] mb-2 flex items-center gap-2"><CheckCircle2 className="h-3 w-3" />Résolus (historique)</div>
            <div className="grid gap-2" data-testid="resolved-incidents">
              {resolved.slice(0, 20).map((r) => (
                <IncidentCard key={r.id} r={r} catalog={catalog} onEdit={openEdit} onDelete={del} compact />
              ))}
            </div>
          </div>
        )}

        {/* Form dialog */}
        {editing && form && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="incident-dialog">
            <div className="bg-white max-w-2xl w-full my-8 p-6 border-2 border-[#0A0A0A]">
              <div className="font-display font-black text-2xl tracking-tighter mb-4">{editing.id ? "Éditer" : "Nouvel"} incident</div>
              <div className="space-y-4">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Titre *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="inc-title" placeholder="Ex: Ralentissement du portail Chef" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Service</Label>
                    <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                      <SelectTrigger data-testid="inc-service"><SelectValue /></SelectTrigger>
                      <SelectContent>{catalog.services.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Sévérité</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                      <SelectTrigger data-testid="inc-severity"><SelectValue /></SelectTrigger>
                      <SelectContent>{catalog.severities.map((s) => <SelectItem key={s} value={s}>{SEVERITY_META[s]?.label || s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Statut</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="inc-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{catalog.statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Message public</Label>
                  <Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inc-message" placeholder="Ce que voient les utilisateurs sur le bandeau" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.show_on_landing} onChange={(e) => setForm({ ...form, show_on_landing: e.target.checked })} data-testid="inc-landing" />
                    Bandeau sur la landing publique
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.show_in_portals} onChange={(e) => setForm({ ...form, show_in_portals: e.target.checked })} data-testid="inc-portals" />
                    Bandeau dans les portails
                  </label>
                </div>
              </div>
              <div className="mt-6 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => { setEditing(null); setForm(null); }} data-testid="inc-cancel">Annuler</Button>
                <Button className="bg-[#E60000] text-white" onClick={save} data-testid="inc-save">Enregistrer</Button>
              </div>
            </div>
          </div>
        )}

        {/* Add update dialog */}
        {addingUpdate && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="update-dialog">
            <div className="bg-white max-w-lg w-full p-6 border-2 border-[#0A0A0A]">
              <div className="font-display font-black text-xl tracking-tighter mb-4">Ajouter un update</div>
              <div className="space-y-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nouveau statut</Label>
                  <Select value={addingUpdate.status} onValueChange={(v) => setAddingUpdate({ ...addingUpdate, status: v })}>
                    <SelectTrigger data-testid="upd-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{catalog.statuses.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s]?.label || s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Message</Label>
                  <Textarea rows={4} value={addingUpdate.message} onChange={(e) => setAddingUpdate({ ...addingUpdate, message: e.target.value })} data-testid="upd-message" />
                </div>
              </div>
              <div className="mt-4 flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setAddingUpdate(null)} data-testid="upd-cancel">Annuler</Button>
                <Button className="bg-[#002FA7] text-white" onClick={addUpdate} data-testid="upd-save">Publier</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function IncidentCard({ r, catalog, onEdit, onDelete, onResolve, onAddUpdate, compact }) {
  const svc = catalog.services.find((s) => s.key === r.service);
  const sev = SEVERITY_META[r.severity] || SEVERITY_META.minor;
  const st = STATUS_META[r.status] || STATUS_META.investigating;
  return (
    <div className={`bg-white border p-4 ${r.resolved ? "border-[#DEE2E6]" : "border-[#FCA5A5]"}`} data-testid={`incident-${r.id}`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${sev.cls}`}>{sev.label}</span>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${st.cls}`}>{st.label}</span>
        <span className="text-[10px] uppercase tracking-wider font-bold bg-[#F1F3F5] px-2 py-0.5">{svc?.label || r.service}</span>
        {r.show_on_landing && <span className="text-[9px] uppercase font-bold bg-[#FFD700] text-[#0A0A0A] px-1.5">Landing</span>}
        {r.show_in_portals && <span className="text-[9px] uppercase font-bold bg-[#002FA7] text-white px-1.5">Portails</span>}
      </div>
      <div className="font-display font-bold">{r.title}</div>
      {r.message && <div className="text-xs text-[#495057] mt-1 whitespace-pre-line">{r.message}</div>}
      <div className="text-[10px] text-[#868E96] mt-2 font-mono">
        Ouvert : {new Date(r.started_at).toLocaleString("fr-FR")}
        {r.resolved_at && ` · Résolu : ${new Date(r.resolved_at).toLocaleString("fr-FR")}`}
      </div>
      {!compact && r.updates?.length > 0 && (
        <div className="mt-3 border-t border-[#DEE2E6] pt-2">
          <div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96] mb-1">Timeline</div>
          <div className="space-y-1 text-xs">
            {r.updates.slice().reverse().slice(0, 5).map((u, i) => (
              <div key={i} className="flex gap-2">
                <span className="font-mono text-[10px] text-[#868E96]">{new Date(u.at).toLocaleString("fr-FR")}</span>
                <span className="font-semibold text-[10px]">{STATUS_META[u.status]?.label || u.status} —</span>
                <span>{u.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-1 flex-wrap">
        {!r.resolved && onAddUpdate && (
          <Button size="sm" variant="outline" onClick={() => onAddUpdate({ id: r.id, status: r.status, message: "" })} data-testid={`add-update-${r.id}`} className="h-8 text-xs"><MessageSquare className="h-3 w-3 mr-1" />Update</Button>
        )}
        {!r.resolved && onResolve && (
          <Button size="sm" onClick={() => onResolve(r)} data-testid={`resolve-${r.id}`} className="h-8 bg-[#059669] text-white text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Résoudre</Button>
        )}
        <Button size="sm" onClick={() => onEdit(r)} data-testid={`edit-${r.id}`} variant="outline" className="h-8 text-xs">Éditer</Button>
        <Button size="sm" onClick={() => onDelete(r)} data-testid={`del-${r.id}`} variant="outline" className="h-8 text-xs border-[#E60000] text-[#E60000] hover:bg-[#E60000] hover:text-white"><Trash2 className="h-3 w-3" /></Button>
      </div>
    </div>
  );
}
